// src/utils/trainingCalendar.ts
import { skillName } from '../data/skills';

/**
 * The training calendar: Estonian days and weeks, on both sides from the same rule.
 *
 * Mirrors `dayKey`, `weeklyFocus` and `nextForm` in functions/src/training.ts, which is the
 * authority. The page computes these to show the focus of the week and the player's form
 * before a single call is made; the server computes them again when it pays.
 */

export type CoreAttribute = 'strength' | 'agility' | 'dexterity' | 'intelligence' | 'endurance';

/** The order the focus rotates in. Must match PHYSICAL in functions/src/training.ts. */
export const CORE_ATTRIBUTES: CoreAttribute[] = ['strength', 'agility', 'dexterity', 'intelligence', 'endurance'];

/** The five names as the skill registry spells them (src/data/skills.ts). */
export const ATTRIBUTE_LABELS = Object.fromEntries(
    CORE_ATTRIBUTES.map((a) => [a, skillName(a)])
) as Record<CoreAttribute, string>;

const TALLINN = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Tallinn', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** YYYY-MM-DD in Tallinn. */
export const dayKey = (now: Date = new Date()): string => TALLINN.format(now);

/** Whole days since 2024-01-01 (a Monday), counted on Tallinn dates. */
const dayIndex = (now: Date): number => {
    const [y, m, d] = dayKey(now).split('-').map(Number);
    return Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(2024, 0, 1)) / 86_400_000);
};

/** The attribute this week favours. Rotates every Monday. */
export const weeklyFocus = (now: Date = new Date()): CoreAttribute => {
    const n = CORE_ATTRIBUTES.length;
    return CORE_ATTRIBUTES[((Math.floor(dayIndex(now) / 7) % n) + n) % n];
};

/** Days until the focus changes (1 on a Sunday). */
export const daysUntilFocusChanges = (now: Date = new Date()): number => 7 - (dayIndex(now) % 7);

/** The form a training session today would leave behind, from the stored form. */
export const nextForm = (
    form: { days?: number; lastDay?: string } | undefined,
    today: string = dayKey()
): { days: number; lastDay: string } => {
    const days = Math.max(0, Math.floor(Number(form?.days ?? 0)));
    const last = typeof form?.lastDay === 'string' ? form.lastDay : '';
    if (last === today) return { days: Math.max(1, days), lastDay: today };
    const [y, m, d] = today.split('-').map(Number);
    const yesterday = new Date(Date.UTC(y, m - 1, d) - 86_400_000).toISOString().slice(0, 10);
    return { days: last === yesterday ? days + 1 : 1, lastDay: today };
};

/** Whether the stored form is still alive today (trained today or yesterday). */
export const formAlive = (form: { days?: number; lastDay?: string } | undefined, today: string = dayKey()): boolean => {
    const last = typeof form?.lastDay === 'string' ? form.lastDay : '';
    if (!last) return false;
    if (last === today) return true;
    const [y, m, d] = today.split('-').map(Number);
    return last === new Date(Date.UTC(y, m - 1, d) - 86_400_000).toISOString().slice(0, 10);
};
